import { zodResolver } from "@hookform/resolvers/zod";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm as useHookForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import TextFieldFormControl from "../../components/base/TextFieldFormControl/textFieldFormControl";
import { AddressCopyButton } from "../../components/buttons/AddressCopyButton";
import { AddConnectedPersonDialog } from "../../components/dialog/AddConnectedPersonDialog/AddConnectedPersonDialog";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog/confirmationDialog";
import { BusinessDataFields } from "../../components/form/BusinessDataFields/BusinessDataFields";
// import { useFormClientSideErrorsHandler } from "../../components/form/ErrorsHandler/ErrorsHandler";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";
import { FormCheckbox } from "../../components/form/FormCheckbox/FormChecbox";
import { COMPANY_ROLE_CHOICES } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { useForm } from "../../hooks/useForm";
import { useToasts } from "../../hooks/useToasts";
import i18n from "../../i18n";
// import {
//   useGetAllClauses,
//   useSetPersonClauseMutation,
// } from "../../services/clausesService";
import {
  useAddCustomerPersonMutation,
  useCompaniesShort,
} from "../../services/customersService";
import { LeadAddDialog } from "../LeadsPage/LeadAddDialog";

import useFoundCustomersByParams from "../../hooks/useFoundCustomersByParams";
import { CustomersFoundDialog } from "./CustomersFoundDialog";

import { AdditionalPhonesDialog } from "./Passport/AdditionalPhonesDialog/AdditionalPhonesDialog";
import { AdditionalEmailsDialog } from "./Passport/AdditionalEmailsDialog/AdditionalEmailsDialog";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MailIcon from "@mui/icons-material/Mail";

import { ActualPersonClausesTable } from "./ActualPersonClausesTable";

// Company roles options

const personFormSchema = z
  .object({
    personName: z.string().optional(),
    personSecondName: z.string().optional(),
    personSurname: z.string().optional(),
    personEmail: z.string().optional().nullable(),
    personPhone: z.string().optional().nullable(),
    personPesel: z.string().optional(),
    personIdCard: z.string().optional(),
    personPassport: z.string().optional(),
    addressLivingStreet: z.string().optional(),
    addressLivingBuilding: z.string().optional(),
    addressLivingApartment: z.string().optional(),
    addressLivingCity: z.string().optional(),
    addressLivingZipcode: z.string().optional(),
    addressMailingStreet: z.string().optional(),
    addressMailingBuilding: z.string().optional(),
    addressMailingApartment: z.string().optional(),
    addressMailingCity: z.string().optional(),
    addressMailingZipcode: z.string().optional(),
    extraNote: z.string().optional(),
    countryMailing: z.string().optional(),
    countryLiving: z.string().optional(),
    // Add JDG fields
    showJdgFields: z.boolean().optional().default(false),
    jdgNip: z.string().optional(),
    jdgRegon: z.string().optional(),
    jdgCompanyName: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Check if any living address field is filled
    const hasAnyLivingAddress = Boolean(
      data.addressLivingStreet ||
      data.addressLivingBuilding ||
      data.addressLivingCity ||
      data.addressLivingZipcode,
    );

    // Cr allow living address to be none
    // If any living address field is filled, all required fields must be filled
    // if (hasAnyLivingAddress) {
    //     if (!data.addressLivingStreet) {
    //         ctx.addIssue({
    //             path: ["addressLivingStreet"],
    //             message: t("form.validations.address_street_required"),
    //             code: z.ZodIssueCode.custom,
    //         });
    //     }
    //     if (!data.addressLivingBuilding) {
    //         ctx.addIssue({
    //             path: ["addressLivingBuilding"],
    //             message: t("form.validations.address_building_required"),
    //             code: z.ZodIssueCode.custom,
    //         });
    //     }
    //     if (!data.addressLivingCity) {
    //         ctx.addIssue({
    //             path: ["addressLivingCity"],
    //             message: t("form.validations.address_city_required"),
    //             code: z.ZodIssueCode.custom,
    //         });
    //     }
    //     if (!data.addressLivingZipcode) {
    //         ctx.addIssue({
    //             path: ["addressLivingZipcode"],
    //             message: t("form.validations.address_zipcode_required"),
    //             code: z.ZodIssueCode.custom,
    //         });
    //     }
    // }
    // Only validate formats if countryLiving is "Poland"
    // Basic validations like required fields still apply regardless of country
    if (data.countryLiving === "Poland") {
      // Phone format validation (Poland specific)
      const phoneRegex = /^\+48\s?\d{3}\s?\d{3}\s?\d{3}$/;
      if (data.personPhone && !phoneRegex.test(data.personPhone)) {
        ctx.addIssue({
          message: t("form.validations.incorrect_phone"),
          code: z.ZodIssueCode.custom,
        });
      }

      // Email format validation
      const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
      if (data.personEmail && !emailRegex.test(data.personEmail)) {
        ctx.addIssue({
          message: t("form.validations.incorrect_email"),
          code: z.ZodIssueCode.custom,
        });
      }

      // PESEL format validation (Poland specific)
      const peselRegex = /^\d{11}$/;
      if (data.personPesel && !peselRegex.test(data.personPesel)) {
        ctx.addIssue({
          message: t("form.validations.incorrect_pesel"),
          code: z.ZodIssueCode.custom,
        });
      }

      // ID Card format validation (Poland specific)
      const idCardRegex = /^[A-Z]{3}\d{6}$/;
      if (data.personIdCard && !idCardRegex.test(data.personIdCard)) {
        ctx.addIssue({
          message: t("form.validations.incorrect_id_card"),
          code: z.ZodIssueCode.custom,
        });
      }

      // Passport format validation (Poland specific)
      const passportRegex = /^[A-Z]{2}\d{6}$/;
      if (data.personPassport && !passportRegex.test(data.personPassport)) {
        ctx.addIssue({
          message: t("form.validations.incorrect_passport"),
          code: z.ZodIssueCode.custom,
        });
      }

      // Zip code format validation (Poland specific)
      const zipCodeRegex = /^\d{2}-\d{3}$/;
      if (
        data.addressLivingZipcode &&
        !zipCodeRegex.test(data.addressLivingZipcode)
      ) {
        ctx.addIssue({
          message: t("form.validations.incorrect_zipcode"),
          code: z.ZodIssueCode.custom,
        });
      }
      if (
        data.addressMailingZipcode &&
        !zipCodeRegex.test(data.addressMailingZipcode)
      ) {
        ctx.addIssue({
          message: t("form.validations.incorrect_zipcode"),
          code: z.ZodIssueCode.custom,
        });
      }

      // NIP validation (Poland specific - 10 digits)
      const nipRegex = /^\d{10}$/;
      if (data.showJdgFields && data.jdgNip && !nipRegex.test(data.jdgNip)) {
        ctx.addIssue({
          message: t("form.validations.incorrect_nip"),
          code: z.ZodIssueCode.custom,
        });
      }

      // REGON validation (Poland specific - 9 or 14 digits)
      const regonRegex = /^(\d{9}|\d{14})$/;
      if (
        data.showJdgFields &&
        data.jdgRegon &&
        !regonRegex.test(data.jdgRegon)
      ) {
        ctx.addIssue({
          message: t("form.validations.incorrect_regon"),
          code: z.ZodIssueCode.custom,
        });
      }
    }
    return z.NEVER;
  });

export function CustomerAddPersonDialog(props) {
  // const { mutate: setPersonClause } = useSetPersonClauseMutation();
  const [relatedPersons, setRelatedPersons] = useState([]);
  const [relatedCompanies, setRelatedCompanies] = useState([]);
  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const { openDialog: openAddConnectedPersonDialog } = useDialogBetter();
  const {
    openDialog: openAddLeadConfirmationDialog,
    closeDialog: closeAddLeadConfirmationDialog,
  } = useDialogBetter();
  const { openDialog: openAddLeadDialog } = useDialogBetter();
  const { openDialog: openCustomersFoundDialog } = useDialogBetter();
  const { openDialog: openAdditionalPhonesForPersonDialog } = useDialogBetter();
  const { openDialog: openAdditionalEmailsForPersonDialog } = useDialogBetter();

  // Add state for input label shrinking
  const [addressFieldsShrink, setAddressFieldsShrink] = useState({
    addressMailingStreet: false,
    addressMailingBuilding: false,
    addressMailingApartment: false,
    addressMailingCity: false,
    addressMailingZipcode: false,
  });

  // Form for adding related company
  const {
    formValue: relatedCompanyForm,
    setFormValue: setRelatedCompanyForm,
    onChange: onChangeRelatedCompany,
  } = useForm({
    company_id: "",
    role: "Owner",
  });

  const { onFormErrors } = useToasts();

  const { control, handleSubmit, watch, setValue, register } = useHookForm({
    resolver: zodResolver(personFormSchema),
    defaultValues: {
      personName: props.defaultData?.personName || "",
      personSurname: props.defaultData?.personSurname || "",
      personEmail: props.defaultData?.personEmail || null,
      personPhone: props.defaultData?.personPhone || null,
      // countryLiving: "Polska",
      // countryMailing: "Polska",
      showJdgFields: false,
    },
  });

  // Copy address function
  const handleAddressCopied = useCallback((values) => {
    setAddressFieldsShrink({
      addressMailingStreet: Boolean(values.addressMailingStreet),
      addressMailingBuilding: Boolean(values.addressMailingBuilding),
      addressMailingApartment: Boolean(values.addressMailingApartment),
      addressMailingCity: Boolean(values.addressMailingCity),
      addressMailingZipcode: Boolean(values.addressMailingZipcode),
    });
  }, []);

  // Additional Emails/Phones form
  const { formValue: additionalPhones, setFormValue: setAdditionalPhones } =
    useForm([""]);
  const { formValue: additionalEmails, setFormValue: setAdditionalEmails } =
    useForm([""]);

  // Old Clauses form
  // const { formValue: clausesFormValue, setFormValue: setClausesFormValue } =
  //   useForm({});
  // const { data: allClauses } = useGetAllClauses();
  
  const { data: companiesShort, isLoading: isLoadingCompanies } =
    useCompaniesShort();

  // Get the selected company details
  const selectedCompany = companiesShort?.results.find(
    (company) => company.id === relatedCompanyForm.company_id,
  );

  // Cr make personName and personSurname optional
  // const personData = watch([
  //     "personName",
  //     "personSurname",
  //     // Cr make address optional
  //     // "addressLivingStreet",
  //     // "addressLivingCity",
  //     // "addressLivingZipcode",
  //     // "addressLivingBuilding",
  // ]).every(Boolean);

  const personEmail = watch("personEmail");
  const personPhone = watch("personPhone");
  //Cr make personData optional
  // const enabledBtn = personData && (personEmail || personPhone);
  const enabledBtn = personEmail || personPhone;

  const personEmails = useMemo(() => {
    let allEmails = [...additionalEmails, personEmail];
    return allEmails;
  }, [personEmail, additionalEmails]);

  const personPhones = useMemo(() => {
    let allphones = [...additionalPhones, personPhone];
    return allphones;
  }, [personPhone, additionalPhones]);

  const { customersFoundedByEmail, customersFoundedByPhone } =
    useFoundCustomersByParams({
      personEmails: personEmails ?? [],
      personPhones: personPhones ?? [],
    });

  const { mutate: addCustomerPerson } = useAddCustomerPersonMutation({
    onError: (error) => {
      toast.custom(
        () => (
          <SnackbarError
            message={t("clients.addPersonDialog.errorAddingClient")}
          />
        ),
        {
          position: "bottom-right",
        },
      );
    },
    onSuccess: (data) => {
      toast.custom(
        () => (
          <SnackbarSuccess
            message={t("clients.addPersonDialog.clientAddedSuccessfully")}
          />
        ),
        {
          position: "bottom-right",
        },
      );

      // Set clauses for the newly created person
      // const formattedClauses = Object.entries(clausesFormValue).map(
      //   ([key, value]) => ({
      //     clauseId:
      //       allClauses?.results.find((clause) => clause.clause_name_eng === key)
      //         ?.clause_id ?? "",
      //     value: value,
      //   }),
      // );

      // Check if those lines are totally unnecessary
      // formattedClauses.forEach((clause) => {
      //   if (data?.person_id) {
      //     setPersonClause({
      //       clauseId: clause.clauseId,
      //       personId: data.person_id,
      //       value: clause.value,
      //     });
      //   }
      // });

      if (props.onSubmitSuccess && data) {
        console.log("add_data", data);
        props.onSubmitSuccess(data);
      }

      if (props.openAddLeadDialogAfterSubmit) {
        openAddLeadConfirmationDialog({
          Component: ConfirmationDialog,
          componentProps: {
            title: t("clients.addPersonDialog.addLeadToClient"),
            onYes: () => {
              openAddLeadDialog({
                Component: LeadAddDialog,
                componentProps: {
                  defaultData: {
                    firstName: watch("personName"),
                    lastName: watch("personSurname"),
                    email: watch("personEmail") || undefined,
                    phone: watch("personPhone") || undefined,
                  },
                  disablePersonFields: true,
                  personId: data?.person_id,
                },
              });
            },
            onNo: closeAddLeadConfirmationDialog,
          },
        });
      }

      props.onClose();
    },
  });

  const handleAddRelatedPerson = useCallback((data) => {
    if (data.existingClient && data.exisitngClientPersonId) {
      // Add existing person
      const newRelatedPerson = {
        person_id: data.exisitngClientPersonId,
        note: data.role || "",
      };
      setRelatedPersons((prev) => [...prev, newRelatedPerson]);
    } else {
      // Add new person
      const newRelatedPerson = {
        person: {
          person_name: data.name || "",
          person_surname: data.surname || "",
          email: {
            email_address: data.email || "",
          },
          phone: {
            phone_number: data.phone || "",
          },
        },
        note: data.role || "",
      };
      setRelatedPersons((prev) => [...prev, newRelatedPerson]);
    }
  }, []);

  const handleAddRelatedCompany = useCallback(() => {
    if (!relatedCompanyForm.company_id || !relatedCompanyForm.role) {
      return;
    }

    const newRelatedCompany = {
      company_id: relatedCompanyForm.company_id,
      role: relatedCompanyForm.role,
    };

    setRelatedCompanies((prev) => [...prev, newRelatedCompany]);
    setRelatedCompanyForm({
      company_id: "",
      role: "Owner",
    });
    setOpenCompanyDialog(false);
  }, [relatedCompanyForm, setRelatedCompanyForm]);

  const handleOpenCompanyDialog = () => {
    setOpenCompanyDialog(true);
  };

  const handleCloseCompanyDialog = () => {
    setOpenCompanyDialog(false);
  };

  const getCompanyName = (companyId) => {
    const company = companiesShort?.results.find((c) => c.id === companyId);
    return company ? company.name : companyId;
  };

  const onSubmit = useCallback(
    (formValue) => {
      // const parsedForm = CustomerPersonFormSchema.safeParse(formValue);

      // if (!parsedForm.success) {
      //     parsedForm.error.issues.forEach((issue) => {
      //         const fieldName = issue.path[0] as string;

      //         const customMessage = `${fieldName}${issue.message}`;

      //         toast.custom(() => (
      //             <SnackbarError
      //                 message={t(`customer.addCustomerForm.${customMessage}`)}
      //             />
      //         ), {
      //             position: "bottom-right",
      //         });
      //     });
      //     return;
      // }

      // Create the payload for the API
      const payload = {
        person: {
          email: formValue.personEmail
            ? {
                email_address: formValue.personEmail,
              }
            : null,
          phone: formValue.personPhone
            ? {
                phone_number: formValue.personPhone,
              }
            : null,
          // clause_decisions: Object.entries(clausesFormValue).map(
          //   ([key, value]) => ({
          //     clause_id:
          //       allClauses?.results.find(
          //         (clause) => clause.clause_name_eng === key,
          //       )?.clause_id ?? "",
          //     decision: value,
          //   }),
          // ),

          person_name: formValue.personName,
          person_name2: formValue.personSecondName,
          person_surname: formValue.personSurname,
          person_pesel: formValue.personPesel,
          person_idcard: formValue.personIdCard,
          person_passport: formValue.personPassport,
        },
        // Add JDG fields if showJdgFields is true
        ...(formValue.showJdgFields
          ? {
              jdg_nip: formValue.jdgNip,
              jdg_regon: formValue.jdgRegon,
              jdg_company_name: formValue.jdgCompanyName,
            }
          : {}),
        related_persons: relatedPersons,
        related_companies: relatedCompanies,
        note: formValue.extraNote || "",
      };

      // Add address_living if it has values
      if (
        formValue.addressLivingCity ||
        formValue.addressLivingZipcode ||
        formValue.addressLivingStreet ||
        formValue.addressLivingBuilding ||
        formValue.addressLivingApartment
      ) {
        payload.address_living = {
          city: formValue.addressLivingCity || "",
          zip_code: formValue.addressLivingZipcode || "",
          street: formValue.addressLivingStreet || "",
          // @ts-ignore
          building: formValue.addressLivingBuilding || "",
          // @ts-ignore
          flat_nr: formValue.addressLivingApartment || "",
        };
      }

      // Add address_corespondence if it has values
      if (
        formValue.addressMailingCity ||
        formValue.addressMailingZipcode ||
        formValue.addressMailingStreet ||
        formValue.addressMailingBuilding ||
        formValue.addressMailingApartment
      ) {
        payload.address_correspondence = {
          city: formValue.addressMailingCity || "",
          zip_code: formValue.addressMailingZipcode || "",
          street: formValue.addressMailingStreet || "",
          building: formValue.addressMailingBuilding || "",
          flat_nr: formValue.addressMailingApartment || "",
        };
      }

      if (props.personId != null) {
        payload.person_id = props.personId;
      }

      if (additionalEmails) {
        payload.additional_emails = additionalEmails.filter((email) => email);
      }

      if (additionalPhones) {
        payload.additional_phones = additionalPhones.filter((phone) => phone);
      }

      addCustomerPerson(payload);
    },
    [
      additionalPhones,
      additionalEmails,
      // clausesFormValue,
      // allClauses,
      relatedPersons,
      relatedCompanies,
      addCustomerPerson,
    ],
  );

  return (
    <>
      <BasicDialog
        open={props.open}
        onClose={props.onClose}
        titleAlign={"center"}
        contentAlign={"center"}
        title={t("clients.addPersonDialog.title")}
        maxWidth={"lg"}
        showDialogActions
      >
        <Grid container direction={"column"}>
          <FormControl>
            {/* Basic data and addresses section - 3 columns */}
            <Grid container spacing={2}>
              {/* Basic Person Data */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" align={"left"}>
                  {t("clients.addPersonDialog.basicData")}
                </Typography>
                <Grid container direction={"column"} spacing={1}>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personName"
                      label={`${t("clients.addPersonDialog.firstName")}`}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personSecondName"
                      label={t("clients.addPersonDialog.middleName")}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personSurname"
                      label={`${t("clients.addPersonDialog.lastName")}`}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personEmail"
                      label={`${t("clients.ClientPassportDialog.primary_email")}`}
                      props={{
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  openAdditionalEmailsForPersonDialog({
                                    Component: AdditionalEmailsDialog,
                                    componentProps: {
                                      personId: null,
                                      formValue: additionalEmails.map(
                                        (email) => ({ email_address: email }),
                                      ),
                                      onSetFormValue: (emails) =>
                                        setAdditionalEmails(emails),
                                    },
                                  });
                                }}
                              >
                                <MailIcon />
                              </IconButton>
                              {customersFoundedByEmail &&
                                customersFoundedByEmail.length > 0 && (
                                  <Tooltip
                                    title={t(
                                      "clients.addPersonDialog.foundCustomers",
                                    )}
                                  >
                                    <ErrorOutlineIcon
                                      color="primary"
                                      style={{
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        openCustomersFoundDialog({
                                          Component: CustomersFoundDialog,
                                          componentProps: {
                                            data: customersFoundedByEmail,
                                          },
                                        })
                                      }
                                    />
                                  </Tooltip>
                                )}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personPhone"
                      label={t("clients.ClientPassportDialog.primary_phone")}
                      props={{
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  openAdditionalPhonesForPersonDialog({
                                    Component: AdditionalPhonesDialog,
                                    componentProps: {
                                      personId: null,
                                      formValue: additionalPhones.map(
                                        (phone) => ({ phone_number: phone }),
                                      ),
                                      onSetFormValue: (phones) =>
                                        setAdditionalPhones(phones),
                                    },
                                  })
                                }
                              >
                                <LocalPhoneIcon />
                              </IconButton>
                              {customersFoundedByPhone &&
                                customersFoundedByPhone.length > 0 && (
                                  <Tooltip
                                    title={t(
                                      "clients.addPersonDialog.foundCustomers",
                                    )}
                                  >
                                    <ErrorOutlineIcon
                                      color="primary"
                                      onClick={() =>
                                        openCustomersFoundDialog({
                                          Component: CustomersFoundDialog,
                                          componentProps: {
                                            data: customersFoundedByPhone,
                                          },
                                        })
                                      }
                                      style={{
                                        cursor: "pointer",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid>
                  {/* <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personPesel"
                      label={t("clients.addPersonDialog.pesel")}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personIdCard"
                      label={t("clients.addPersonDialog.idCard")}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="personPassport"
                      label={t("clients.addPersonDialog.passport")}
                    />
                  </Grid> */}
                </Grid>
              </Grid>

              {/* Living Address */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" align={"left"}>
                    {t("clients.addPersonDialog.livingAddress")}
                  </Typography>
                  <AddressCopyButton
                    form={{ watch, setValue }}
                    sourcePrefix="addressLiving"
                    targetPrefix="addressMailing"
                    fields={[
                      "Street",
                      "Building",
                      "Apartment",
                      "City",
                      "Zipcode",
                    ]}
                    onCopy={handleAddressCopied}
                    tooltipText={t(
                      "clients.addPersonDialog.copyFromLivingAddress",
                    )}
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Grid container direction={"column"} spacing={1}>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressLivingStreet"
                      label={`${t("clients.addPersonDialog.street")}`}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressLivingBuilding"
                      label={`${t("clients.addPersonDialog.buildingNumber")}`}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressLivingApartment"
                      label={t("clients.addPersonDialog.apartmentNumber")}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressLivingCity"
                      label={`${t("clients.addPersonDialog.city")}`}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressLivingZipcode"
                      label={`${t("clients.addPersonDialog.postalCode")}`}
                    />
                  </Grid>
                  <ControlledSelect
                    control={control}
                    name="countryLiving"
                    label={t("clients.addPersonDialog.country")}
                    data={[
                      {
                        label: t("clients.addPersonDialog.poland"),
                        value: "Poland",
                      },
                      {
                        label: t("clients.addPersonDialog.other"),
                        value: "Other",
                      },
                    ]}
                  />
                </Grid>
              </Grid>

              {/* Mailing Address */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" align={"left"}>
                  {t("clients.addPersonDialog.correspondenceAddress")}
                </Typography>
                <Grid container direction={"column"} spacing={1}>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressMailingStreet"
                      label={t("clients.addPersonDialog.street")}
                      props={{
                        InputLabelProps: {
                          shrink: addressFieldsShrink.addressMailingStreet,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressMailingBuilding"
                      label={t("clients.addPersonDialog.buildingNumber")}
                      props={{
                        InputLabelProps: {
                          shrink: addressFieldsShrink.addressMailingBuilding,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressMailingApartment"
                      label={t("clients.addPersonDialog.apartmentNumber")}
                      props={{
                        InputLabelProps: {
                          shrink: addressFieldsShrink.addressMailingApartment,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressMailingCity"
                      label={t("clients.addPersonDialog.city")}
                      props={{
                        InputLabelProps: {
                          shrink: addressFieldsShrink.addressMailingCity,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="addressMailingZipcode"
                      label={t("clients.addPersonDialog.postalCode")}
                      props={{
                        InputLabelProps: {
                          shrink: addressFieldsShrink.addressMailingZipcode,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <ControlledSelect
                      control={control}
                      name="countryMailing"
                      label={t("clients.addPersonDialog.country")}
                      data={[
                        {
                          label: t("clients.addPersonDialog.poland"),
                          value: "Poland",
                        },
                        {
                          label: t("clients.addPersonDialog.other"),
                          value: "Other",
                        },
                      ]}
                    />
                  </Grid>

                  {/* Add Business Data Fields */}
                  <BusinessDataFields
                    register={register}
                    control={control}
                    showFieldsName="showJdgFields"
                    showInitially={false}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Divider variant="middle" sx={{ my: 2 }} />

            {/* Clauses and Extra Info - Second row */}
            <Grid container spacing={2}>
              {/* Cr hide clauses */}
              {/* Clauses */}
              {/* <Grid item xs={12} md={6}>
                                <Typography variant="h6" align={"left"}>
                                    {t("clients.addPersonDialog.clauses")}
                                </Typography>
                                <Grid container sx={{ mt: 2 }} rowGap={1} direction={"column"}>
                                    {allClauses?.results.map((clause) => (
                                        <FormCheckbox
                                            key={clause.clause_id}
                                            formValue={clausesFormValue}
                                            checked={Boolean(
                                                clausesFormValue[clause.clause_name_eng],
                                            )}
                                            onChange={(e, checked) =>
                                                // @ts-ignore
                                                setClausesFormValue((prev) => ({
                                                    ...prev,
                                                    [clause.clause_name_eng]: checked,
                                                }))
                                            }
                                            name={clause.clause_name_eng}
                                            label={clause.clause_name_eng}
                                        />
                                    ))}
                                </Grid>
                            </Grid> */}

              {/* Extra Info */}
                <Grid item xs={6}>
                    <Typography variant="h6" align={"left"}>
                    {t("clients.addPersonDialog.additionalInfo")}
                  </Typography>
                </Grid>
                {props.actualClauses && (<Grid item xs={6}>
                    <Typography variant={"h6"}>
                        {t("clients.ClientPassportDialog.consents")}
                    </Typography>
                </Grid>)}
              <Grid item xs={12} container columnSpacing={3}>
                <Grid container item xs={6} direction={"column"} spacing={2}>
                  <Grid item>
                    <ControlledTextField
                      control={control}
                      name="extraNote"
                      label={t("clients.addPersonDialog.note")}
                      props={{
                        fullWidth: true,
                        InputProps: {
                          rows: 4,
                          multiline: true,
                          inputComponent: "textarea",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          openAddConnectedPersonDialog({
                            Component: AddConnectedPersonDialog,
                            componentProps: {
                              onSubmit: handleAddRelatedPerson,
                            },
                          });
                        }}
                      >
                        {t("clients.addPersonDialog.addRelatedPerson")}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleOpenCompanyDialog}
                      >
                        {t("clients.addPersonDialog.addCompany")}
                      </Button>
                    </Grid>
                  </Grid>
                  {relatedPersons.length > 0 && (
                    <Grid item>
                      <Typography variant="subtitle1">
                        {t("clients.addPersonDialog.relatedPersons", {
                          count: relatedPersons.length,
                        })}
                      </Typography>
                      {relatedPersons.map((person, index) => (
                        <Typography key={index} variant="body2">
                          {"person_id" in person
                            ? `Istniejący klient${person.note || ""}`
                            : `${person.person?.person_name || ""} ${
                                person.person?.person_surname || ""
                              }`}
                          {person.note ? ` - ${person.note}` : ""}
                        </Typography>
                      ))}
                    </Grid>
                  )}
                  {relatedCompanies.length > 0 && (
                    <Grid item>
                      <Typography variant="subtitle1">
                        {t("clients.addPersonDialog.relatedCompanies", {
                          count: relatedCompanies.length,
                        })}
                      </Typography>
                      {relatedCompanies.map((company, index) => (
                        <Typography key={index} variant="body2">
                          {getCompanyName(company.company_id)} - {company.role}
                        </Typography>
                      ))}
                    </Grid>
                  )}
                </Grid>
                  {props.actualClauses && (
                    <Grid item xs={6}>
                      <ActualPersonClausesTable actualClauses={props.actualClauses}/>
                    </Grid>
                  )}
              </Grid>
            </Grid>
            {/* Submit Button */}
            <Grid item xs={12} sx={{ mt: 4 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSubmit(onSubmit, onFormErrors)}
                disabled={!enabledBtn}
              >
                {t("clients.addPersonDialog.addClient")}
              </LoadingButton>
            </Grid>
          </FormControl>
        </Grid>
      </BasicDialog>
      {/* Dialog for adding company */}
      <Dialog
        open={openCompanyDialog}
        onClose={handleCloseCompanyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("clients.addPersonDialog.addCompany")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  {t("clients.addPersonDialog.company")}
                </Typography>
                <Select
                  value={relatedCompanyForm.company_id}
                  onChange={onChangeRelatedCompany}
                  name="company_id"
                  fullWidth
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t("clients.addPersonDialog.selectCompany")}
                  </MenuItem>
                  {companiesShort?.results.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {selectedCompany && (
              <>
                <Grid item xs={12}>
                  <TextFieldFormControl
                    value={selectedCompany.company_short_address}
                    name="company_address"
                    label={t("clients.addPersonDialog.address")}
                    readOnly
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldFormControl
                    value={selectedCompany.company_nip}
                    name="company_nip"
                    label={t("clients.addPersonDialog.nip")}
                    readOnly
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  {t("clients.addPersonDialog.function")}
                </Typography>
                <Select
                  value={relatedCompanyForm.role}
                  onChange={onChangeRelatedCompany}
                  name="role"
                  fullWidth
                >
                  {COMPANY_ROLE_CHOICES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {i18n.t(`company.roles.${role}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={handleCloseCompanyDialog} sx={{ mr: 1 }}>
                {t("clients.addPersonDialog.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddRelatedCompany}
                disabled={
                  !relatedCompanyForm.company_id || !relatedCompanyForm.role
                }
              >
                {t("clients.addPersonDialog.connectWithCompany")}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}
